import React from 'react';
import { Box, Button, Typography, Grid } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckIcon from '@mui/icons-material/Check';
import {useNavigate} from "react-router";

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export function DayViewDetail({ appointments, date ,setOpenAppointmentDetail }) {
    const navigate = useNavigate()

    const handleRedirect = (url) => {
        navigate(url)
    }
  return (
      <Grid container spacing={2.5}>
        {appointments.length > 0 ? (
            appointments.map((appointment, index) => {
                const isPaid = appointment.payment_status === 'Paid';
                const staffAssigned = appointment.staff_name && appointment.staff_name !== 'Staff Not Assigned';
                return (
                <Grid item lg={3} md={4} sm={6} xs={12} key={appointment.id} sx={{cursor:"pointer"}} onClick={() => setOpenAppointmentDetail(appointment.id)}>
                  <Box
                      sx={{
                        background: '#fff',
                        border: '1px solid #EFE3EA',
                        borderLeft: `5px solid ${appointment.status_color}`,
                        borderRadius: '14px',
                        boxShadow: '0 2px 10px rgba(15,23,42,0.05)',
                        p: { xs: 2, sm: 2.5 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                        '&:hover': { boxShadow: '0 6px 20px rgba(15,23,42,0.10)', transform: 'translateY(-1px)' },
                      }}
                  >
                    {/* Top: avatar + name/time + payment badge */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '12px',
                        background: appointment.status_color, color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: '0.95rem', flexShrink: 0,
                      }}>
                        {getInitials(appointment.customer.name)}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontWeight: 700, fontSize: '1.05rem', color: '#1a1a2e',
                          lineHeight: 1.25, textTransform: 'capitalize', wordBreak: 'break-word',
                        }}>
                          {appointment.customer.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, color: '#A72C68' }} />
                          <Typography sx={{ fontSize: '0.85rem', color: '#6B7280' }}>
                            {appointment.start_time} – {appointment.end_time}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
                        background: isPaid ? '#E8F5E9' : '#FDECEF',
                        color: isPaid ? '#2e7d32' : '#D8345F',
                        borderRadius: '999px', px: 1, py: 0.4,
                        fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                      }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: isPaid ? '#2e7d32' : '#D8345F' }} />
                        {appointment.payment_status}
                      </Box>
                    </Box>

                    {/* Service chips */}
                    {appointment.services?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 2 }}>
                        {appointment.services.map((service, i) => (
                          <Box key={i} sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%',
                            background: '#FBE8F1', color: '#A72C68', border: '1px solid #F3D3E1',
                            borderRadius: '8px', px: 1, py: 0.5, fontSize: '12px', fontWeight: 600,
                          }}>
                            <CheckIcon sx={{ fontSize: 14 }} />
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>
                              {service}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Type / Consultation row */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1, mt: 1.5,
                      background: '#F4F2F8', border: '1px solid #E9E6F0',
                      borderRadius: '10px', px: 1.5, py: 1.25,
                    }}>
                      <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: '#5B5F97' }} />
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#3F4257', textTransform: 'capitalize' }}>
                        {appointment.type}
                      </Typography>
                    </Box>

                    {appointment.notes && (
                        <Typography sx={{
                          color: 'rgba(0, 0, 0, 0.6)', mt: 1, fontSize: '0.875rem',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {appointment.notes}
                        </Typography>
                    )}

                    {/* Spacer pushes footer to bottom */}
                    <Box sx={{ flexGrow: 1 }} />

                    {/* Divider + Staff / Assign footer */}
                    <Box sx={{ borderTop: '1px solid #EEE', my: 1.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box sx={{
                          width: 34, height: 34, borderRadius: '50%', border: '1px solid #E5E7EB',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', flexShrink: 0,
                        }}>
                          <PersonOutlineIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', color: '#9CA3AF', textTransform: 'uppercase' }}>
                            Staff
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.85rem', fontWeight: 600,
                            color: staffAssigned ? appointment.staff_color : '#D97706',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {staffAssigned ? appointment.staff_name : 'Not assigned'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                );
            })
        ) : (
            <Grid item xs={12}>
              <Button
                  sx={{
                    background: '#A72C68',
                    px: '24px',
                    border: '1px solid #A72C68',
                    color: '#fff',
                    marginRight: 2,
                    marginBottom: { xl: 0, lg: 1, md: 2, sm: 2, xs: 2 },
                    '@media (min-width: 700px) and (max-width: 1199px)': { marginRight: '0px' },
                    "&:hover": {
                      background: '#fff',
                      color: '#A72C68',
                    }
                  }}
                  onClick={() => {
                       handleRedirect("/crm/add-appointment"); // Uncomment if you have this function
                  }}
              >
                Add Appointment
              </Button>
            </Grid>
        )}
      </Grid>
  );
}
